import React from "react"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ValueMappingDisplay } from "@/components/shared/value-mapping-display"
import type { FlatMapping } from "@/types/ui"
import { ChevronDown, ChevronRight, Pencil, Plus, X } from "lucide-react"
import { StatusPill, hubTh, hubTr } from "@/components/hub/hub-canvas"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

// Generate stable key for mapping rows
const getMappingKey = (mapping: FlatMapping): string => {
  return mapping.destinationField
}

/** The value-count pill reads differently once a default value mapping is configured */
const valuesPillKey = (count: number, defaultValue: FlatMapping["defaultValueMapping"]): TranslationKey => {
  const hasDefault = defaultValue !== undefined && defaultValue !== null
  const key = hasDefault
    ? count === 1 ? "destinationFields.table.valuesWithDefaultOne" : "destinationFields.table.valuesWithDefaultOther"
    : count === 1 ? "destinationFields.table.valuesOne" : "destinationFields.table.valuesOther"
  return key
}

interface MappingsTableProps {
  filteredMappings: FlatMapping[]
  expandedRows: Set<string>
  onToggleRowExpansion: (key: string) => void
  onEditMapping: (mapping: FlatMapping) => void
  onRemoveMapping: (entityIndex: number | null, mappingIndex: number | null) => void
  onAddMapping?: (mapping: FlatMapping) => void
}

export function MappingsTable({
  filteredMappings,
  expandedRows,
  onToggleRowExpansion,
  onEditMapping,
  onRemoveMapping,
  onAddMapping
}: MappingsTableProps) {
  const { t, n } = useTranslation()

  return (
    <div className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-8"></TableHead>
            <TableHead className={`${hubTh} w-1/3`}>{t("destinationFields.table.destinationField")}</TableHead>
            <TableHead className={`${hubTh} w-1/6`}>{t("destinationFields.table.entity")}</TableHead>
            <TableHead className={`${hubTh} w-1/4`}>{t("destinationFields.table.sourceField")}</TableHead>
            <TableHead className={`${hubTh} w-28 text-right`}></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredMappings.map((mapping) => {
            const mappingKey = getMappingKey(mapping)
            return (
            <React.Fragment key={mappingKey}>
              <TableRow className={`${hubTr} ${mapping.isInvalid ? "bg-destructive/5" : mapping.isMapped ? "" : "text-muted-foreground"}`}>
                <TableCell>
                  {mapping.valueMappings && mapping.valueMappings.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onToggleRowExpansion(mappingKey)}
                      className="h-6 w-6"
                    >
                      {expandedRows.has(mappingKey) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`font-mono text-xs ${mapping.isMapped ? "font-semibold text-foreground" : ""}`}>{mapping.destinationField}</span>
                    {mapping.isInvalid && <StatusPill tone="danger">{t("destinationFields.table.invalid")}</StatusPill>}
                    {mapping.valueMappings && mapping.valueMappings.length > 0 && (
                      <StatusPill>{t(valuesPillKey(mapping.valueMappings.length, mapping.defaultValueMapping), { count: n(mapping.valueMappings.length) })}</StatusPill>
                    )}
                    {mapping.valueMappingSetName && (
                      <StatusPill tone="warning" title={t("destinationFields.table.setTitle")}>{t("destinationFields.table.set", { name: mapping.valueMappingSetName })}</StatusPill>
                    )}
                    {mapping.dataTypeConverter && <StatusPill tone="active">{mapping.dataTypeConverter}</StatusPill>}
                  </div>
                  {mapping.codeTableName && <div className="mt-0.5 text-[11px] text-muted-foreground">{t("destinationFields.table.codeTable", { name: mapping.codeTableName })}</div>}
                </TableCell>
                <TableCell className="py-2">
                  {mapping.entityName ? <StatusPill>{mapping.entityName}</StatusPill> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="py-2 font-mono text-xs">
                  {mapping.sourceField ? <span className="text-foreground">{mapping.sourceField}</span> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {mapping.isMapped ? (
                    <div className="flex justify-end space-x-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        title={t("destinationFields.table.edit")}
                        onClick={() => onEditMapping(mapping)}
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">{t("destinationFields.table.edit")}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t("common.actions.remove")}
                        onClick={() => onRemoveMapping(mapping.entityIndex, mapping.mappingIndex)}
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">{t("common.actions.remove")}</span>
                      </Button>
                    </div>
                  ) : (
                    onAddMapping && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        title={t("destinationFields.table.add")}
                        onClick={() => onAddMapping(mapping)}
                      >
                        <Plus className="h-4 w-4" />
                        <span className="sr-only">{t("destinationFields.table.add")}</span>
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
              {expandedRows.has(mappingKey) && mapping.valueMappings && (
                <TableRow key={`${mappingKey}-expanded`} className={mapping.isInvalid ? "bg-destructive/10" : ""}>
                  <TableCell></TableCell>
                  <TableCell colSpan={4}>
                    <div className="bg-muted/20 p-4 rounded-md">
                      <h4 className="font-medium mb-2">{t("destinationFields.table.valueMappings")}</h4>
                      <div className="space-y-2">
                        {mapping.valueMappings.map((valueMapping, vmIndex) => (
                          <ValueMappingDisplay
                            key={vmIndex}
                            sourceValue={valueMapping.sourceValue}
                            destinationValue={valueMapping.destinationValue}
                            codeTableName={mapping.codeTableName}
                          />
                        ))}
                        {mapping.defaultValueMapping !== undefined && mapping.defaultValueMapping !== null && (
                          <ValueMappingDisplay
                            sourceValue={t("destinationFields.table.defaultValue")}
                            destinationValue={mapping.defaultValueMapping}
                            codeTableName={mapping.codeTableName}
                          />
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
